// /join-us — the public homepage for logged-out visitors and the top
// "Join" tab of the anonymous sidebar. It's the default destination for a
// signed-out visit to the site root (see routes/index.tsx).
//
// Visually it reuses the start-circle photo-hero landing. Its "Join" CTA
// leads to /get-started — the join-network signup rendered inside this same
// public shell. `hideWordmark` drops the landing's own wordmark because the
// surrounding public shell already carries the brand.

import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { I } from '@leanin/ui';
import { PhotoHeroLanding } from '@/components/entry-journeys';
import { useEntryJourneyCopy } from '@/components/entry-journeys/journey-copy';

export const Route = createFileRoute('/_public/join-us')({
  // Signed-in visitors are already members — send them to the feed (a
  // mid-onboarding visitor is caught by the _public layout's onboarding gate
  // first). They never see the anonymous sidebar, so the "Join" tab is a
  // logged-out surface only.
  beforeLoad: ({ context }) => {
    if (context.session !== null) {
      throw redirect({ to: '/feed' });
    }
  },
  component: JoinUsPage,
});

function JoinUsPage() {
  const navigate = useNavigate();
  const t = useTranslations('EntryJourneys');
  // Only the start-circle PHOTO is borrowed for the visual — the words are
  // this page's own. The ad journeys each pitch one offering; this is the
  // universal front door, so its copy spans the whole product: one point per
  // sidebar destination (Circles, Networks, Resources) plus the movement's
  // scale, with icons matching the nav so the cards echo the sidebar.
  const copy = useEntryJourneyCopy('start-circle');
  // The start-circle journey always resolves a photo; the guard keeps the
  // types honest (the copy hook types the photo optional across journeys).
  if (copy.photo === undefined) return null;
  const items = [
    {
      icon: I.circles,
      title: t('joinUs.whatsAhead.item1.title'),
      body: t('joinUs.whatsAhead.item1.body'),
    },
    {
      icon: I.network,
      title: t('joinUs.whatsAhead.item2.title'),
      body: t('joinUs.whatsAhead.item2.body'),
    },
    {
      icon: I.book,
      title: t('joinUs.whatsAhead.item3.title'),
      body: t('joinUs.whatsAhead.item3.body'),
    },
    {
      icon: I.heart,
      title: t('joinUs.whatsAhead.item4.title'),
      body: t('joinUs.whatsAhead.item4.body'),
    },
  ];
  return (
    <PhotoHeroLanding
      headline={t('joinUs.headline')}
      subhead={t('joinUs.subhead')}
      photo={copy.photo}
      {...(copy.photoPosition !== undefined ? { photoPosition: copy.photoPosition } : {})}
      items={items}
      // Own CTA label too — "Join" echoes the sidebar tab this page lives
      // under, rather than the start-circle journey's "Get started".
      ctaLabel={t('joinUs.cta')}
      hideWordmark
      // Inside the app shell the page renders at real viewport heights, and
      // this surface carries the long benefit copy: clamp the hero to the
      // fold and lay the points out underneath. The ad landings keep the
      // default two-column `aside`.
      whatsAhead="below"
      onCtaClick={() => void navigate({ to: '/get-started' })}
    />
  );
}
